import { addMonths, differenceInCalendarMonths, format, isValid, parseISO } from 'date-fns';
import type { ActivityPoint } from '@/types/api';

/** `2026-07` -> `Jul 2026`. Falls back to the raw value if it will not parse. */
export function formatMonth(period: string): string {
  const date = parseISO(`${period}-01`);
  return isValid(date) ? format(date, 'MMM yyyy') : period;
}

/**
 * The API only returns months that saw activity, but a time axis that silently skips empty months
 * misleads — February sitting next to November reads as steady activity. Charts therefore always
 * work on a gap-filled series where quiet months exist with an explicit zero.
 */
export function fillMonthGaps(points: ActivityPoint[]): ActivityPoint[] {
  if (points.length === 0) return [];
  const sorted = [...points].sort((a, b) => a.period.localeCompare(b.period));
  const first = parseISO(`${sorted[0].period}-01`);
  const last = parseISO(`${sorted[sorted.length - 1].period}-01`);
  // Unparseable periods, or a corrupt span that would explode into thousands of buckets, fall
  // back to the raw (sorted) series rather than guessing.
  if (!isValid(first) || !isValid(last) || differenceInCalendarMonths(last, first) > 600) {
    return sorted;
  }
  const byPeriod = new Map(sorted.map((point) => [point.period, point.count]));
  const filled: ActivityPoint[] = [];
  for (let cursor = first; cursor <= last; cursor = addMonths(cursor, 1)) {
    const period = format(cursor, 'yyyy-MM');
    filled.push({ period, count: byPeriod.get(period) ?? 0 });
  }
  return filled;
}

/** Running total of the same series — the catalogue's size at the end of each month. */
export function cumulate(points: ActivityPoint[]): ActivityPoint[] {
  let total = 0;
  return points.map((point) => ({ period: point.period, count: (total += point.count) }));
}

/** Count for one calendar month (`yyyy-MM`); 0 when that month saw no activity. */
export function countForMonth(points: ActivityPoint[], period: string): number {
  return points.find((point) => point.period === period)?.count ?? 0;
}
